import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import AngelIcon from '@/components/base/AngelIcon';

type QuestionType = 'text' | 'choice' | 'multiline';

interface Question {
  id: string;
  type: QuestionType;
}

const questions: Question[] = [
  { id: 'q1', type: 'multiline' },
  { id: 'q2', type: 'choice' },
  { id: 'q3', type: 'choice' },
  { id: 'q4', type: 'choice' },
  { id: 'q5', type: 'choice' },
  { id: 'q6', type: 'choice' },
  { id: 'q7', type: 'choice' },
  { id: 'q8', type: 'choice' },
  { id: 'q9', type: 'choice' },
  { id: 'q10', type: 'multiline' },
  { id: 'q11', type: 'choice' },
  { id: 'q12', type: 'multiline' },
  { id: 'q13', type: 'multiline' },
  { id: 'q14', type: 'choice' },
  { id: 'q15', type: 'multiline' },
  { id: 'q16', type: 'choice' },
];

// High-weight commitment questions
const highWeightIds = new Set(['q3', 'q4', 'q5', 'q7', 'q8', 'q9', 'q11']);
// Medium-weight self-awareness questions
const mediumWeightIds = new Set(['q1', 'q2', 'q10', 'q15']);

// Positive answers (indicate readiness) for choice questions
const positiveAnswers: Record<string, Set<string>> = {
  q3: new Set(['願意', 'Yes']),
  q4: new Set(['願意', 'Yes']),
  q5: new Set(['願意', 'Yes']),
  q6: new Set(['生命', 'Life', '兩者皆是', 'Both']),
  q7: new Set(['願意', 'Yes']),
  q8: new Set(['願意', 'Yes']),
  q9: new Set(['願意', 'Yes']),
  q11: new Set(['願意', 'Yes']),
  q14: new Set(['願意（公開）', '願意（匿名）', 'Yes (Public)', 'Yes (Anonymous)']),
  q16: new Set(['準備好了', 'Ready']),
};

export default function Qualification() {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [result, setResult] = useState<number | null>(null);

  const totalQuestions = questions.length;
  const isLastQuestion = currentStep === totalQuestions - 1;
  const currentQ = questions[currentStep];

  const setAnswer = (value: string) => {
    setAnswers((prev) => ({ ...prev, [currentQ.id]: value }));
  };

  const canNext = () => {
    const answer = answers[currentQ.id];
    return answer && answer.trim().length > 0;
  };

  const handleNext = () => {
    if (isLastQuestion) {
      // Calculate readiness score
      const score = calculateReadiness(answers);
      setResult(score);
    } else {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const calculateReadiness = (allAnswers: Record<string, string>): number => {
    let totalWeight = 0;
    let earned = 0;

    questions.forEach((q) => {
      const answer = allAnswers[q.id] || '';
      if (!answer.trim()) return;

      let weight = 1; // supplementary default
      if (highWeightIds.has(q.id)) weight = 3;
      else if (mediumWeightIds.has(q.id)) weight = 2;

      totalWeight += weight;

      if (q.type === 'choice') {
        const positives = positiveAnswers[q.id];
        if (positives && positives.has(answer)) {
          earned += weight;
        } else if (q.id === 'q16') {
          // q16: 'Almost ready' gets partial
          if (answer === '快準備好了' || answer === 'Almost ready') earned += weight * 0.5;
        } else if (q.id === 'q2') {
          // q2: higher Hawkins levels get more weight
          const highLevels = ['勇氣 (200)', '中性 (250)', '接受性 (350)', '理性 (400)', '衷愛 (500)', '喜悅 (600+)',
            'Courage (200)', 'Neutrality (250)', 'Acceptance (350)', 'Reason (400)', 'Love (500)', 'Joy (600+)'];
          if (highLevels.includes(answer)) earned += weight;
          else if (answer === '不確定' || answer === 'Not sure') earned += weight * 0.3;
        } else if (q.id === 'q6') {
          if (answer === '兩者皆是' || answer === 'Both') earned += weight;
          else if (answer === '生命' || answer === 'Life') earned += weight * 0.8;
        } else if (q.id === 'q14') {
          if (answer === '願意（匿名）' || answer === 'Yes (Anonymous)') earned += weight * 0.8;
          else if (answer === '願意（公開）' || answer === 'Yes (Public)') earned += weight;
        }
      } else {
        // Text/multiline: check length as proxy for engagement
        if (answer.length >= 50) earned += weight;
        else if (answer.length >= 10) earned += weight * 0.5;
      }
    });

    return totalWeight > 0 ? Math.round((earned / totalWeight) * 100) : 0;
  };

  const renderQuestionContent = () => {
    const questionData = currentQ;

    if (questionData.type === 'multiline') {
      return (
        <div className="space-y-3">
          <textarea
            value={answers[questionData.id] || ''}
            onChange={(e) => setAnswer(e.target.value)}
            maxLength={300}
            rows={5}
            className="w-full px-4 py-3 rounded-md border border-foreground-200 text-sm bg-background-50 focus:outline-none focus:ring-1 focus:ring-accent-400 resize-none"
            placeholder={t(`qualification.questions.${questionData.id}.placeholder`)}
          />
          <p className="text-xs text-foreground-400 text-right">
            {(answers[questionData.id] || '').length}/300
          </p>
        </div>
      );
    }

    if (questionData.type === 'choice') {
      const options: string[] = i18n.getResource(
        i18n.language,
        'translation',
        `qualification.questions.${questionData.id}.options`
      ) as string[] || [];
      const currentAnswer = answers[questionData.id] || '';

      return (
        <div className="space-y-2">
          {options.map((option: string) => (
            <button
              key={option}
              onClick={() => setAnswer(option)}
              className={`w-full text-left px-4 py-3 rounded-md border text-sm transition-all cursor-pointer ${
                currentAnswer === option
                  ? 'bg-accent-100/60 border-accent-400 text-accent-900'
                  : 'bg-background-50 border-foreground-200 text-foreground-600 hover:border-foreground-300'
              }`}
            >
              {option}
            </button>
          ))}
          {questionData.id === 'q2' && (
            <p className="text-xs text-foreground-400 italic mt-1">
              {t(`qualification.questions.${questionData.id}.hint`)}
            </p>
          )}
        </div>
      );
    }

    // text type fallback
    return (
      <input
        type="text"
        value={answers[questionData.id] || ''}
        onChange={(e) => setAnswer(e.target.value)}
        className="w-full px-4 py-3 rounded-md border border-foreground-200 text-sm bg-background-50 focus:outline-none focus:ring-1 focus:ring-accent-400"
        placeholder={t(`qualification.questions.${questionData.id}.placeholder`)}
      />
    );
  };

  // ── Result Screen ──────────────────────────────────────
  if (result !== null) {
    const isReady = result >= 85;
    const isAlmostReady = result >= 70 && result < 85;
    const showAwakeners = result >= 90;

    return (
      <div className="min-h-screen bg-background-50 text-foreground-900">
        <Navbar />
        <main>
          <section className="max-w-2xl mx-auto px-4 md:px-6 pt-28 md:pt-32 pb-20">
            <div className="text-center mb-10">
              <AngelIcon variant="gold" size="lg" />
              <h1
                className="text-2xl md:text-3xl mt-4 mb-2 text-foreground-900"
                style={{ fontFamily: 'var(--font-heading)' }}
              >
                {t('qualification.result.title')}
              </h1>
              <p className="text-xs text-foreground-400">{t('qualification.result.titleEn')}</p>
            </div>

            {/* Readiness Score */}
            <div className="bg-background-100/40 border border-foreground-200/50 rounded-lg p-8 md:p-10 text-center mb-8">
              <div className="text-5xl md:text-6xl font-bold text-accent-500 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
                {result}%
              </div>
              <p className="text-xs text-foreground-400 uppercase tracking-wider mb-6">Journey Readiness</p>

              <p className="text-base md:text-lg text-foreground-900 leading-relaxed mb-2">
                {isReady
                  ? t('qualification.result.ready')
                  : isAlmostReady
                  ? t('qualification.result.almostReady')
                  : t('qualification.result.notReady')}
              </p>
              <p className="text-xs text-foreground-400 mb-1">
                {isReady
                  ? t('qualification.result.readyEn')
                  : isAlmostReady
                  ? t('qualification.result.almostReadyEn')
                  : t('qualification.result.notReadyEn')}
              </p>
            </div>

            {/* Pricing Reveal — only if ready */}
            {isReady && (
              <>
                <div className="bg-background-100/40 border border-accent-200/50 rounded-lg p-8 md:p-10 text-center mb-8">
                  <h2
                    className="text-xl md:text-2xl mb-2 text-foreground-900"
                    style={{ fontFamily: 'var(--font-heading)' }}
                  >
                    {t('qualification.pricing.title')}
                  </h2>
                  <p className="text-xs text-foreground-400 mb-1">{t('qualification.pricing.titleEn')}</p>
                  <p className="text-sm text-foreground-500 mb-6">{t('qualification.pricing.subtitle')}</p>

                  <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6">
                    <div className="border border-foreground-200/60 rounded-lg p-5 w-full sm:w-48 text-center">
                      <p className="text-xs text-foreground-400 mb-1">{t('qualification.pricing.regular')}</p>
                      <p className="text-2xl font-bold text-foreground-400 line-through" style={{ fontFamily: 'var(--font-heading)' }}>
                        {t('qualification.pricing.regularPrice')}
                      </p>
                    </div>
                    <div className="border-2 border-accent-300 bg-accent-50/30 rounded-lg p-5 w-full sm:w-48 text-center">
                      <p className="text-xs text-accent-600 mb-1">{t('qualification.pricing.founding')}</p>
                      <p className="text-2xl font-bold text-accent-600" style={{ fontFamily: 'var(--font-heading)' }}>
                        {t('qualification.pricing.foundingPrice')}
                      </p>
                    </div>
                  </div>

                  <Link
                    to="/membership"
                    className="whitespace-nowrap inline-flex px-10 py-3.5 rounded-md bg-primary-500 text-background-50 text-sm md:text-base hover:bg-primary-600 transition-colors cursor-pointer"
                  >
                    {t('qualification.pricing.cta')}
                  </Link>
                </div>

                {/* Future Journeys — Hidden */}
                <div className="bg-background-100/30 border border-foreground-100/40 rounded-lg p-6 text-center mb-8">
                  <p className="text-sm text-foreground-400 italic">
                    {t('qualification.pricing.futureJourneys')}
                  </p>
                  <p className="text-xs text-foreground-300 mt-1">
                    {t('qualification.pricing.futureJourneysEn')}
                  </p>
                </div>

                {/* Awakeners Cohort — only if high readiness */}
                {showAwakeners && (
                  <div className="bg-background-100/40 border border-foreground-200/50 rounded-lg p-8 md:p-10 text-center mb-8">
                    <p className="text-xs text-foreground-400 mb-2">{t('qualification.pricing.awakenersNote')}</p>
                    <h3
                      className="text-lg md:text-xl mb-2 text-foreground-900"
                      style={{ fontFamily: 'var(--font-heading)' }}
                    >
                      {t('qualification.pricing.awakenersName')}
                    </h3>
                    <p className="text-xs text-foreground-400 mb-1">{t('qualification.pricing.awakenersNameEn')}</p>
                    <p className="text-sm text-foreground-500 mb-4">{t('qualification.pricing.awakenersDesc')}</p>
                    <p className="text-2xl font-bold text-foreground-900 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                      {t('qualification.pricing.awakenersPrice')}
                    </p>
                    <button className="whitespace-nowrap px-6 py-2.5 rounded-md border border-foreground-200 text-foreground-600 text-sm hover:bg-foreground-50 transition-colors cursor-pointer">
                      {t('qualification.pricing.awakenersCta')}
                    </button>
                  </div>
                )}
              </>
            )}

            {/* Return to Free Angel — if not ready */}
            {!isReady && (
              <div className="text-center">
                <Link
                  to="/free-angel"
                  className="whitespace-nowrap inline-flex px-8 py-3 rounded-md border border-foreground-200 text-foreground-600 text-sm hover:bg-foreground-50 transition-colors cursor-pointer"
                >
                  {t('qualification.result.returnToFreeAngel')}
                </Link>
              </div>
            )}
          </section>
        </main>
        <Footer />
      </div>
    );
  }

  // ── Question Screen ─────────────────────────────────────
  return (
    <div className="min-h-screen bg-background-50 text-foreground-900">
      <Navbar />
      <main>
        <section className="max-w-2xl mx-auto px-4 md:px-6 pt-28 md:pt-32 pb-20">
          {/* Header */}
          <div className="text-center mb-8">
            <p className="text-xs tracking-[0.2em] uppercase text-foreground-400 mb-4">
              {t('qualification.title')} &middot; {t('qualification.titleEn')}
            </p>
            <h1
              className="text-2xl md:text-3xl mb-2 text-foreground-900"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              {t('qualification.title')}
            </h1>
            <p className="text-sm text-foreground-500 max-w-md mx-auto mb-1">
              {t('qualification.subtitle')}
            </p>
            <p className="text-xs text-foreground-400 mb-4">{t('qualification.subtitleEn')}</p>
            <Link
              to="/qualification-slides"
              className="whitespace-nowrap inline-flex items-center gap-2 px-5 py-2.5 rounded-md border border-accent-300 bg-accent-50/40 text-accent-700 text-sm hover:bg-accent-100 hover:border-accent-400 transition-colors cursor-pointer"
            >
              <i className="ri-file-pdf-line text-base" />
              下載離線 PDF 簡報
              <span className="text-xs text-foreground-400 ml-1">（16 頁 · 列印友善）</span>
            </Link>
          </div>

          {/* Angel note */}
          <div className="flex items-center justify-center gap-2 text-xs text-foreground-400 mb-8">
            <AngelIcon variant="default" size="sm" />
            <span>{t('qualification.aiAngelNote')}</span>
          </div>

          {/* Progress bar */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-2">
              <span className="text-xs text-foreground-400">
                {t('qualification.progress').replace('{current}', String(currentStep + 1)).replace('{total}', String(totalQuestions))}
              </span>
              <span className="text-xs text-foreground-400">{Math.round(((currentStep + 1) / totalQuestions) * 100)}%</span>
            </div>
            <div className="w-full h-1.5 bg-foreground-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-accent-500 rounded-full transition-all duration-500"
                style={{ width: `${((currentStep + 1) / totalQuestions) * 100}%` }}
              />
            </div>
          </div>

          {/* Question */}
          <div className="bg-background-100/40 border border-foreground-200/50 rounded-lg p-6 md:p-8 mb-8">
            <div className="flex items-start gap-3 mb-5">
              <span className="text-accent-500 font-medium text-sm shrink-0 mt-0.5">
                Q{currentStep + 1}
              </span>
              <h2
                className="text-lg md:text-xl text-foreground-900 leading-relaxed"
                style={{ fontFamily: 'var(--font-heading)' }}
              >
                {t(`qualification.questions.${currentQ.id}.label`)}
              </h2>
            </div>
            <p className="text-xs text-foreground-400 ml-8 mb-6">
              {t(`qualification.questions.${currentQ.id}.labelEn`)}
            </p>

            <div className="ml-8">
              {renderQuestionContent()}
            </div>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between">
            <button
              onClick={handlePrev}
              disabled={currentStep === 0}
              className={`whitespace-nowrap px-4 py-2 rounded-md text-sm transition-colors cursor-pointer ${
                currentStep === 0
                  ? 'text-foreground-300 cursor-not-allowed'
                  : 'text-foreground-600 hover:text-foreground-900 hover:bg-foreground-50'
              }`}
            >
              <i className="ri-arrow-left-line mr-1" />
              Back
            </button>

            <button
              onClick={handleNext}
              disabled={!canNext()}
              className={`whitespace-nowrap px-6 py-2.5 rounded-md text-sm transition-colors cursor-pointer ${
                canNext()
                  ? 'bg-accent-500 text-background-50 hover:bg-accent-600'
                  : 'bg-foreground-100 text-foreground-400 cursor-not-allowed'
              }`}
            >
              {isLastQuestion ? (
                <>
                  完成旅程準備度 <i className="ri-check-line ml-1" />
                </>
              ) : (
                <>
                  Next <i className="ri-arrow-right-line ml-1" />
                </>
              )}
            </button>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}